import * as PopoverPrimitive from '@radix-ui/react-popover'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import * as React from 'react'

import { cn } from '../lib/utils'

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverAnchor = PopoverPrimitive.Anchor

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-popover-content-transform-origin]',
        className
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
))
PopoverContent.displayName = PopoverPrimitive.Content.displayName

// Motion-animated popover content: a spring scale+fade that grows from the anchor
// corner (the Radix transform-origin), so it reads as springing out of the trigger.
// Radix's Content stays the Floating-UI-positioned wrapper (its transform is left
// untouched) while an inner motion.div does the scale — animating scale on the Content
// itself would fight Radix's positioning transform. forceMount + AnimatePresence let
// the exit animation play on close. Pass the controlled `open` so presence is in sync.
interface MotionPopoverContentProps extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> {
  open: boolean
}

const MotionPopoverContent = ({ open, className, children, align = 'center', sideOffset = 4, ...props }: MotionPopoverContentProps) => {
  const reduced = useReducedMotion()
  return (
    <PopoverPrimitive.Portal forceMount>
      <PopoverPrimitive.Content forceMount align={align} sideOffset={sideOffset} className="z-50 outline-none" {...props}>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={reduced ? false : { opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.92 }}
              transition={reduced ? { duration: 0 } : { type: 'spring', duration: 0.4, bounce: 0.25 }}
              style={{ transformOrigin: 'var(--radix-popover-content-transform-origin)' }}
              className={cn('rounded-md bg-popover p-4 text-popover-foreground', className)}>
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
}

export { Popover, PopoverTrigger, PopoverContent, MotionPopoverContent, PopoverAnchor }
